import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  DEFAULT_DISPLAY_NAME,
  MAX_SUGGESTED_PROMPTS,
  PROMPT_MANAGEMENT_CATALOG,
  VOICE_CATALOG,
  formatGreetingMessage,
  formalityLevels,
  getMirrorSiteTerminologyDescription,
  productDisplays,
  sentenceStyles,
  suggestedPromptLabels,
  type GreetingTemplate,
  type ProductDisplay,
  type SentenceStyle,
  type SuggestedPromptLabel,
} from 'skipify-types';

import { ConfigSection } from '../../../common';
import { AgenticPronunciationEntry } from '../../../store/modules/merchant/types';
import { useEllaDraft } from '../EllaDraftContext';
import { FORM_MAX_WIDTH, exampleBoxSx, radioSx, selectionBoxSx } from '../ellaFormStyles';

const EXAMPLE_BOX_SX = exampleBoxSx('#F5F5F5');

/** Pronunciation entry fields are required non-empty strings in MMS (`min(1)`); no max is defined in catalog. */
const PRONUNCIATION_FIELD_MIN_LENGTH = 1;
const PRONUNCIATION_EXAMPLE = VOICE_CATALOG.pronunciationGlossary.exampleEntries[0];
const PRONUNCIATION_SECTION_DESCRIPTION = `Add words your agent should say a specific way — brand names, product names, anything easy to mispronounce. Use phonetic spelling (e.g., '${PRONUNCIATION_EXAMPLE.text}' → 'SKIP-ih-fye')`;

const EMPTY_PRONUNCIATION_ENTRY: AgenticPronunciationEntry = { text: '', pronunciation: '' };

const SENTENCE_STYLE_SUBLABELS: Record<SentenceStyle, string> = {
  brief: '1-2 sentences',
  balanced: 'Short paragraphs',
  detailed: 'Full explanations',
};

const PRODUCT_DISPLAY_COPY: Record<ProductDisplay, { title: string; description: string }> = {
  In_Chat: {
    title: 'In-chat carousel',
    description:
      "Products that are recommended show as an interactive carousel inside the agent's chat and scroll inline as your agent responds",
  },
  StoreFront: {
    title: 'Full-page takeover',
    description:
      'Your agent opens a dedicated, full-page view when recommending or comparing products.',
  },
};

const capitalize = (value?: string | null) => {
  if (!value) {
    return '';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const PromptManagementForm = (): JSX.Element => {
  const { draft, readOnly, updateDraft } = useEllaDraft();
  const { promptManagement } = draft;
  const primaryAgentName = draft.personalities?.[0]?.displayName.trim() || DEFAULT_DISPLAY_NAME;

  const formality = promptManagement.formality;
  const sentenceStyle = promptManagement.sentenceStyle;
  const greeting = promptManagement.customGreeting as GreetingTemplate;
  const mirrorSiteTerminology = promptManagement.brandVocabulary.mirrorSiteTerminology;
  const productDisplay = draft.productExperience.productDisplay;

  const prompts = useMemo<(SuggestedPromptLabel | '')[]>(() => {
    const existing = promptManagement.suggestedPrompts;
    return Array.from({ length: MAX_SUGGESTED_PROMPTS }, (_, index) => existing[index] ?? '');
  }, [promptManagement.suggestedPrompts]);

  const pronunciationEntries = useMemo<AgenticPronunciationEntry[]>(() => {
    const entries = draft.voice?.pronunciationGlossary ?? [];
    return entries.length > 0 ? entries : [{ ...EMPTY_PRONUNCIATION_ENTRY }];
  }, [draft.voice?.pronunciationGlossary]);

  const duplicatePronunciationTextIndexes = useMemo(() => {
    const seen = new Map<string, number>();
    const duplicates = new Set<number>();

    pronunciationEntries.forEach((entry, index) => {
      const key = entry.text.trim();
      if (!key) {
        return;
      }
      const firstIndex = seen.get(key);
      if (firstIndex !== undefined) {
        duplicates.add(firstIndex);
        duplicates.add(index);
        return;
      }
      seen.set(key, index);
    });

    return duplicates;
  }, [pronunciationEntries]);

  const formalityIndex = Math.max(formalityLevels.indexOf(formality) + 1, 1);
  const formalityDescription =
    PROMPT_MANAGEMENT_CATALOG.formalityLevels.descriptions[formality] ?? '';
  const formalityExample = PROMPT_MANAGEMENT_CATALOG.formalityLevels.examples[formality] ?? '';
  const sentenceExample = PROMPT_MANAGEMENT_CATALOG.sentenceStyles.examples[sentenceStyle] ?? '';

  const greetingOptions = useMemo(
    () =>
      PROMPT_MANAGEMENT_CATALOG.greetingTemplates.values.map((templateKey) => ({
        key: templateKey,
        label: formatGreetingMessage(templateKey, primaryAgentName),
      })),
    [primaryAgentName],
  );

  const onPromptChange = (index: number, value: SuggestedPromptLabel | '') => {
    const next = [...prompts];
    next[index] = value;
    const filtered = next.filter((prompt): prompt is SuggestedPromptLabel => prompt !== '');
    updateDraft((prev) => ({
      ...prev,
      promptManagement: { ...prev.promptManagement, suggestedPrompts: filtered },
    }));
  };

  const onPronunciationChange = (
    index: number,
    field: keyof AgenticPronunciationEntry,
    value: string,
  ) => {
    updateDraft((prev) => {
      const current =
        prev.voice?.pronunciationGlossary && prev.voice.pronunciationGlossary.length > 0
          ? prev.voice.pronunciationGlossary
          : [{ ...EMPTY_PRONUNCIATION_ENTRY }];
      const next = [...current];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, voice: { pronunciationGlossary: next } };
    });
  };

  const onAddPronunciation = () => {
    updateDraft((prev) => {
      const current =
        prev.voice?.pronunciationGlossary && prev.voice.pronunciationGlossary.length > 0
          ? prev.voice.pronunciationGlossary
          : [{ ...EMPTY_PRONUNCIATION_ENTRY }];
      return {
        ...prev,
        voice: { pronunciationGlossary: [...current, { ...EMPTY_PRONUNCIATION_ENTRY }] },
      };
    });
  };

  const onRemovePronunciation = (index: number) => {
    updateDraft((prev) => {
      const current = prev.voice?.pronunciationGlossary ?? [];
      if (current.length <= 1) {
        return { ...prev, voice: { pronunciationGlossary: [{ ...EMPTY_PRONUNCIATION_ENTRY }] } };
      }
      return { ...prev, voice: { pronunciationGlossary: current.filter((_, i) => i !== index) } };
    });
  };

  return (
    <Box
      component="form"
      id="ella-prompt-management-form"
      data-testid="ella-prompt-management-form"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      {/* Brand vocabulary */}
      <ConfigSection
        title="Brand vocabulary"
        description="Select whether you want the agent to use your storefront's terminology whenever possible, or use clear universal shopping language."
        marginBottom={2}
      >
        <RadioGroup
          value={mirrorSiteTerminology ? 'true' : 'false'}
          onChange={(_e, value) =>
            updateDraft((prev) => ({
              ...prev,
              promptManagement: {
                ...prev.promptManagement,
                brandVocabulary: { mirrorSiteTerminology: value === 'true' },
              },
            }))
          }
          sx={{ maxWidth: FORM_MAX_WIDTH, gap: 2 }}
        >
          {(
            [
              {
                value: 'true',
                title: "Use storefront's terminology",
                description: getMirrorSiteTerminologyDescription(true),
              },
              {
                value: 'false',
                title: 'Use universal language',
                description: getMirrorSiteTerminologyDescription(false),
              },
            ] as const
          ).map((option) => (
            <Box
              key={option.value}
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Radio
                size="small"
                value={option.value}
                checked={mirrorSiteTerminology === (option.value === 'true')}
                disabled={readOnly}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    m: 0,
                    color: 'primary.dark',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 16,
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: 'normal',
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  variant="small"
                  color="text.secondary"
                  sx={{ m: 0, lineHeight: '20px' }}
                >
                  {option.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </ConfigSection>

      {/* Formality */}
      <ConfigSection
        title="Formality"
        description="Set the shopping agent's tone, from casual and playful (1) to refined and elevated (5)."
        marginBottom={2}
      >
        <Box
          sx={{ display: 'flex', gap: '10px', mb: 2.5 }}
          role="radiogroup"
          aria-label="Formality"
        >
          {formalityLevels.map((level, index) => {
            const isSelected = formality === level;
            return (
              <Box
                key={level}
                component="button"
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={`Formality level ${index + 1}: ${level}`}
                disabled={readOnly}
                onClick={() =>
                  updateDraft((prev) => ({
                    ...prev,
                    promptManagement: { ...prev.promptManagement, formality: level },
                  }))
                }
                sx={{
                  ...selectionBoxSx(isSelected, readOnly),
                  width: 80,
                  height: 52,
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '27px',
                  letterSpacing: 0,
                }}
              >
                {index + 1}
              </Box>
            );
          })}
        </Box>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
          Formality example: {capitalize(formalityDescription)} ({formalityIndex})
        </Typography>
        <Box sx={EXAMPLE_BOX_SX}>
          <Typography variant="small" color="text.primary">
            &ldquo;{formalityExample}&rdquo;
          </Typography>
        </Box>
      </ConfigSection>

      {/* Message Length & Verbosity */}
      <ConfigSection
        title="Message Length & Verbosity"
        description="Select an option of how concise or detailed the shopping agent's responses should be."
        marginBottom={2}
      >
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 2.5 }}
          role="radiogroup"
          aria-label="Message Length & Verbosity"
        >
          {sentenceStyles.map((style) => {
            const isSelected = sentenceStyle === style;
            return (
              <Box
                key={style}
                component="button"
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={capitalize(style)}
                disabled={readOnly}
                onClick={() =>
                  updateDraft((prev) => ({
                    ...prev,
                    promptManagement: { ...prev.promptManagement, sentenceStyle: style },
                  }))
                }
                sx={{
                  ...selectionBoxSx(isSelected, readOnly),
                  minWidth: 160,
                  height: 72,
                  px: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: '21px',
                    color: 'inherit',
                  }}
                >
                  {capitalize(style)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: '18px',
                    color: 'inherit',
                    opacity: 0.85,
                  }}
                >
                  {SENTENCE_STYLE_SUBLABELS[style]}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
          Example response: {capitalize(sentenceStyle)}
        </Typography>
        <Box
          sx={{
            ...exampleBoxSx('#FFFFFF'),
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography variant="small" color="text.secondary">
            Shopper asked: &quot;What&apos;s your return policy?&quot;
          </Typography>
          <Box
            sx={{
              backgroundColor: '#F5F5F5',
              border: '1px solid',
              borderColor: '#E0E0E0',
              borderRadius: '4px',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="small" color="text.primary">
              {sentenceExample}
            </Typography>
          </Box>
        </Box>
      </ConfigSection>

      {/* Greeting & Prompts */}
      <ConfigSection
        title="Greeting & Prompts"
        description="Your agent greeting, along with up to three button prompts, will display on the first agent screen."
        marginBottom={2}
      >
        <FormControl fullWidth sx={{ maxWidth: FORM_MAX_WIDTH, mb: 3 }} disabled={readOnly}>
          <InputLabel id="ella-greeting-label" shrink>
            Select a greeting
          </InputLabel>
          <Select
            labelId="ella-greeting-label"
            label="Select a greeting"
            notched
            value={greeting}
            onChange={(e: SelectChangeEvent) =>
              updateDraft((prev) => ({
                ...prev,
                promptManagement: { ...prev.promptManagement, customGreeting: e.target.value },
              }))
            }
            displayEmpty
          >
            {greetingOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Prompts
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: FORM_MAX_WIDTH }}>
          {prompts.map((prompt, index) => (
            <FormControl key={`prompt-${index}`} fullWidth disabled={readOnly}>
              <InputLabel id={`ella-prompt-${index}-label`} shrink>
                {`Prompt ${index + 1}`}
              </InputLabel>
              <Select
                labelId={`ella-prompt-${index}-label`}
                label={`Prompt ${index + 1}`}
                notched
                value={prompt}
                displayEmpty
                onChange={(e: SelectChangeEvent) =>
                  onPromptChange(index, e.target.value as SuggestedPromptLabel | '')
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {suggestedPromptLabels.map((label) => (
                  <MenuItem
                    key={label}
                    value={label}
                    disabled={prompts.includes(label) && prompts[index] !== label}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
      </ConfigSection>

      {/* Pronunciation Guide */}
      <ConfigSection
        title="Pronunciation Guide"
        description={PRONUNCIATION_SECTION_DESCRIPTION}
        marginBottom={2}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxWidth: FORM_MAX_WIDTH,
          }}
        >
          {pronunciationEntries.map((entry, index) => {
            const isDuplicateText = duplicatePronunciationTextIndexes.has(index);
            const isOnlyEmptyRow =
              pronunciationEntries.length === 1 && !entry.text && !entry.pronunciation;
            return (
              <Box
                key={`pronunciation-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1.5,
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Word or phrase"
                  value={entry.text}
                  onChange={(e) => onPronunciationChange(index, 'text', e.target.value)}
                  disabled={readOnly}
                  error={isDuplicateText}
                  helperText={isDuplicateText ? 'This word or phrase is already used' : undefined}
                  FormHelperTextProps={{ sx: { mx: 0 } }}
                  inputProps={{
                    'aria-label': `Pronunciation word ${index + 1}`,
                    minLength: PRONUNCIATION_FIELD_MIN_LENGTH,
                  }}
                  sx={{ flex: 1 }}
                />
                <Box
                  aria-hidden
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  <ArrowForwardIcon
                    fontSize="small"
                    sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  placeholder="Pronounced as"
                  value={entry.pronunciation}
                  onChange={(e) => onPronunciationChange(index, 'pronunciation', e.target.value)}
                  disabled={readOnly}
                  inputProps={{
                    'aria-label': `Pronunciation spelling ${index + 1}`,
                    minLength: PRONUNCIATION_FIELD_MIN_LENGTH,
                  }}
                  sx={{ flex: 1 }}
                />
                {!isOnlyEmptyRow && (
                  <IconButton
                    aria-label={`Remove pronunciation ${index + 1}`}
                    onClick={() => onRemovePronunciation(index)}
                    disabled={readOnly}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}

          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              disabled={readOnly}
              onClick={onAddPronunciation}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                px: 2.5,
                color: 'text.primary',
                borderColor: 'divider',
              }}
            >
              Add pronunciation
            </Button>
          </Box>
        </Box>
      </ConfigSection>

      {/* Product Display */}
      <ConfigSection
        title="Product Display"
        description="Choose how the shopping agent shows products to shoppers."
        marginBottom={2}
      >
        <RadioGroup
          value={productDisplay}
          onChange={(_e, value) =>
            updateDraft((prev) => ({
              ...prev,
              productExperience: {
                ...prev.productExperience,
                productDisplay: value as ProductDisplay,
              },
            }))
          }
          sx={{ maxWidth: FORM_MAX_WIDTH, gap: 2 }}
        >
          {productDisplays.map((display) => (
            <Box
              key={display}
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Radio
                size="small"
                value={display}
                checked={productDisplay === display}
                disabled={readOnly}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    m: 0,
                    color: 'primary.dark',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 16,
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: 'normal',
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  {PRODUCT_DISPLAY_COPY[display].title}
                </Typography>
                <Typography
                  variant="small"
                  color="text.secondary"
                  sx={{ m: 0, lineHeight: '20px' }}
                >
                  {PRODUCT_DISPLAY_COPY[display].description}
                </Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </ConfigSection>
    </Box>
  );
};
